import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { format } from 'date-fns'
import JoinGroupModal from './JoinGroupModal'

interface MyGroupsProps {
  userId: string
}

interface GroupMember {
  group_id: string
  joined_at: string
  groups: {
    id: string
    name: string
    description: string | null
    group_type?: string | null
  }
}

export default function MyGroups({ userId }: MyGroupsProps) {
  const queryClient = useQueryClient()
  const [showJoinModal, setShowJoinModal] = useState(false)

  const { data: groups, isLoading } = useQuery({
    queryKey: ['user-groups', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('group_members')
        .select(`
          group_id,
          joined_at,
          groups (
            id,
            name,
            description,
            group_type
          )
        `)
        .eq('user_id', userId)
        .order('joined_at', { ascending: false })

      if (error) throw error
      
      // Transform data: Supabase returns groups as array, extract first element
      const transformed = (data || []).map((member: any) => ({
        group_id: member.group_id,
        joined_at: member.joined_at,
        groups: Array.isArray(member.groups) ? member.groups[0] : member.groups,
      })) as GroupMember[]
      
      // Filter out vendor groups (broadcaster and ad-creator groups)
      return transformed.filter((member) => {
        const group = member.groups
        // Only include groups with no group_type or group_type that's not broadcaster/ad-creator
        return !group?.group_type || (group.group_type !== 'broadcaster' && group.group_type !== 'ad-creator')
      })
    },
  })

  const leaveGroup = useMutation({
    mutationFn: async (groupId: string) => {
      const { error } = await supabase
        .from('group_members')
        .delete()
        .eq('user_id', userId)
        .eq('group_id', groupId)

      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-groups', userId] })
    },
  })

  const handleLeaveGroup = async (groupId: string) => {
    if (confirm('Are you sure you want to leave this group?')) {
      await leaveGroup.mutateAsync(groupId)
    }
  }

  const handleJoinGroup = () => {
    setShowJoinModal(true)
  }

  if (isLoading) {
    return (
      <div 
        className="rounded-lg p-6 border-2"
        style={{
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
        }}
      >
        <p style={{ color: 'var(--text-primary)' }}>Loading groups...</p>
      </div>
    )
  }

  return (
    <div 
      className="rounded-lg p-6 border-2"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: 'var(--bg-tertiary)',
        color: 'var(--text-primary)',
      }}
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          My Groups
        </h2>
        <button
          onClick={handleJoinGroup}
          className="px-4 py-2 font-semibold rounded"
          style={{
            backgroundColor: 'var(--bg-interactive)',
            color: 'var(--text-inverse)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--bg-interactive-hover)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--bg-interactive)'
          }}
        >
          Join a Group
        </button>
      </div>

      {!groups || groups.length === 0 ? (
        <p style={{ color: 'var(--text-primary)' }}>
          You are not a member of any groups yet.
        </p>
      ) : (
        <div className="space-y-4">
          {groups.map((member) => {
            const group = member.groups
            if (!group) return null

            const joinedDate = format(new Date(member.joined_at), 'dd/MM/yyyy')

            return (
              <div
                key={member.group_id}
                className="border-b-2 pb-4 last:border-b-0 last:pb-0"
                style={{ borderColor: 'var(--bg-tertiary)' }}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 
                      className="font-semibold text-lg mb-1"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {group.name}
                    </h3>
                    {group.description && (
                      <p 
                        className="text-sm mb-2"
                        style={{ color: 'var(--text-primary)' }}
                      >
                        {group.description}
                      </p>
                    )}
                    <p 
                      className="text-sm"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      Member {joinedDate}
                    </p>
                  </div>
                  <button
                    onClick={() => handleLeaveGroup(member.group_id)}
                    className="hover:underline font-semibold whitespace-nowrap"
                    style={{ color: 'var(--color-error)' }}
                    aria-label={`Leave ${group.name}`}
                  >
                    Leave
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {showJoinModal && (
        <JoinGroupModal
          onClose={() => setShowJoinModal(false)}
          onSuccess={() => {
            setShowJoinModal(false)
            // Queries will be invalidated automatically by the modal
          }}
        />
      )}
    </div>
  )
}

